import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { AlertSeverity } from '../../../cop/store/rsd';
import { v4 as uuidv4 } from 'uuid';
import { RatingArea, RatingScale } from '@app/modules/management/store/ratings/ratings.model';
import { ColorUtilsService } from '@app/core/services';
import { API_BASE_URL } from '@app/core/constants/api-base-url';
import { HttpService } from 'src/app/modules/core/services/http.service';
import { UserContextService } from '@app/modules/core/services/user-context.service';

interface LoadedRatingScale extends RatingScale {
  organizationUId: string;
}

@Injectable({ providedIn: 'root' })
export class AlertStatusService {
  private translate = inject(TranslateService);
  private colorUtils = inject(ColorUtilsService);
  private http = inject(HttpService);
  private userContext = inject(UserContextService).getContext;

  private severityItems = [
    {
      severity: AlertSeverity.Minimal,
      backColor: '#d6d5dd',
      textColor: '#ffffff',
      label: this.translate.instant('MAP.SEVERITY-BAR.ALERT-SEVERITY-' + AlertSeverity.Minimal),
      name: this.translate.instant('NOTIFICATION-PREFERENCES.MINIMAL-HEADING'),
      description: this.translate.instant('NOTIFICATION-PREFERENCES.MINIMAL-DETAILS')
    },
    {
      severity: AlertSeverity.Low,
      backColor: '#94d82d',
      textColor: '#ffffff',
      label: this.translate.instant('MAP.SEVERITY-BAR.ALERT-SEVERITY-' + AlertSeverity.Low),
      name: this.translate.instant('NOTIFICATION-PREFERENCES.LOW-HEADING'),
      description: this.translate.instant('NOTIFICATION-PREFERENCES.LOW-DETAILS')
    },
    {
      severity: AlertSeverity.Moderate,
      backColor: '#ffd43b',
      textColor: '#1e1B31',
      label: this.translate.instant('MAP.SEVERITY-BAR.ALERT-SEVERITY-' + AlertSeverity.Moderate),
      name: this.translate.instant('NOTIFICATION-PREFERENCES.MODERATE-HEADING'),
      description: this.translate.instant('NOTIFICATION-PREFERENCES.MODERATE-DETAILS')
    },
    {
      severity: AlertSeverity.High,
      backColor: '#ff8129',
      textColor: '#ffffff',
      label: this.translate.instant('MAP.SEVERITY-BAR.ALERT-SEVERITY-' + AlertSeverity.High),
      name: this.translate.instant('NOTIFICATION-PREFERENCES.HIGH-HEADING'),
      description: this.translate.instant('NOTIFICATION-PREFERENCES.HIGH-DETAILS')
    },
    {
      severity: AlertSeverity.Extreme,
      backColor: '#e03131',
      textColor: '#ffffff',
      label: this.translate.instant('MAP.SEVERITY-BAR.ALERT-SEVERITY-' + AlertSeverity.Extreme),
      name: this.translate.instant('NOTIFICATION-PREFERENCES.EXTREME-HEADING'),
      description: this.translate.instant('NOTIFICATION-PREFERENCES.EXTREME-DETAILS')
    }
  ];

  ratingScale: LoadedRatingScale;

  public getDefaultSeverityItems(): {
    severity: AlertSeverity;
    backColor: string;
    textColor: string;
    label: string;
    name: string;
    description: string;
  }[] {
    return this.severityItems;
  }

  public async loadAlertRatingScale(): Promise<void> {
    const { organizationUId } = this.userContext();

    if (this.ratingScale?.organizationUId === organizationUId) {
      return;
    }

    const customRatingScale = await firstValueFrom(this.http.get(`${API_BASE_URL}/v1/meta/ratings/area/${RatingArea.Alert}`)).catch(err => {
      console.error(err);
      return null;
    });

    if (!!customRatingScale) {
      this.ratingScale = {
        ...customRatingScale,
        levels: customRatingScale.levels?.map(level => ({
          ...level,
          textColor: this.colorUtils.isColorLightOrDark(level.htmlColorCode) === 'light' ? '#1e1B31' : '#ffffff'
        })),
        organizationUId
      };
    } else {
      // default RatingScale
      this.ratingScale = {
        uId: uuidv4(),
        organizationUId,
        name: 'Default Alert Rating Scale',
        levels: this.severityItems.map((s, index) => ({
          uId: uuidv4(),
          displayLevel: index + 1,
          level: index + 1,
          label: s.label,
          name: s.name,
          description: s.description,
          htmlColorCode: s.backColor,
          textColor: s.textColor,
          cultureCode: this.translate.getCurrentLang()
        })),
        ratingArea: RatingArea.Alert,
        mapping: []
      };
    }
  }

  unload(): void {
    this.ratingScale = null;
  }
}
